import { Button, Card } from "antd";
import Avatar from "antd/lib/avatar/avatar";
import React, { useEffect, useState } from "react";
import Loadable from "react-loadable";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
    APPS_ROUTE,
    CONTACTS_ROUTE,
    DASHBOARD_ROUTE,
    DIARY_ROUTE,
    DOCUMENTS_ROUTE,
    ORGANIZATIONS_ROUTE,
    PROJECTS_ROUTE,
    SERVICES_FEES_ROUTE,
    SETTINGS_ROUTE,
    TASKS_ROUTE,
} from "../../constants/appRoutes";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { AppManager } from "../../manager";
import { RootState } from "../../redux/reducers";
import { Organization } from "../../models/Organization/Organization";
import AppLoadingPage from "../AppLoadingPage/AppLoadingPage";
import AppSimplePage from "../AppSimplePage/AppSimplePage";
import Styles from "./index.scss";
import Loading from "../../components/Loading/Loading";
import AusplanAppsPage from "./Apps/Apps";
import WelcomePage from "./Welcome/WelcomePage";
import { useModelEventWatcher } from "../../hooks/modelHook";
import { TFullDealerGroupAttr, TOrganizationAttr } from "../../models/Organization/Types";
import SubscriptionIssuePage from "./SubscriptionIssuePage";
import { DealerGroup } from "../../models/Organization/DealerGroup";
import AuthorizedOrganizationsPage, { SelectOrganizationView } from "./AllOrganizations/AuthorizedOrganizationsPage";

const PageInitializing: React.FC<{ text?: string }> = ({ text }) => {
    return <MainLayout Content={<Loading text={text || "Initializing"} />} />;
};

const DashboardPage = Loadable({
    loader: () => import("./Dashboard/Dashboard"),
    loading: () => <PageInitializing text="Initializing Dashboard Page" />,
});

const ContactsPage = Loadable({
    loader: () => import("./Contacts/ContactsPage"),
    loading: () => <PageInitializing text="Initializing Contacts Page" />,
});

const DocumentsPage = Loadable({
    loader: () => import("./Documents/DocumentsPage"),
    loading: () => <PageInitializing text="Initializing Documents Page" />,
});

const ServicesFeesPage = Loadable({
    loader: () => import("./ServicesFees/ServicesFeesPage"),
    loading: () => <PageInitializing text="Initializing Services Fees Page" />,
});

const DiaryPage = Loadable({
    loader: () => import("./Diary/DiaryPage"),
    loading: () => <PageInitializing text="Initializing Diary Page" />,
});

const TasksPage = Loadable({
    loader: () => import("./Tasks/TasksPage"),
    loading: () => <PageInitializing text="Initializing Tasks Page" />,
});

const SettingsPage = Loadable({
    loader: () => import("./Settings/SettingsPage"),
    loading: () => <PageInitializing text="Initializing Settings Page" />,
});

const ProtectedRoutes = () => {
    const [initializing, setInitializing] = useState(true);
    const activeDGroup = useSelector<RootState, DealerGroup>((state) => state.dealerGroup.activeDealerGroup!);
    const [dGroupAttr] = useModelEventWatcher(activeDGroup, "ATTRIBUTES_UPDATED", activeDGroup.data.attrs as TFullDealerGroupAttr);
    const location = useLocation();
    const initializeApp = async () => {
        await Promise.all([
            AppManager.contact.apiFetchAllContacts(),
            AppManager.document.main.apiFetchAllDocuments(() => {}),
            AppManager.document.template.apiFetchAllTemplates(() => {}),
            AppManager.task.apiFetchAllTasks("user", () => {}),
            AppManager.diary.apiFetchAllMySchedules(() => {}),
            AppManager.task.apiFetchAllTasks("assigned", () => {}),
            AppManager.project.apiFetchAllProjects("user", () => {}),
            AppManager.project.apiFetchAllProjects("org", () => {}),
            AppManager.project.template.apiFetchAllTemplates(() => {}),
        ]);
        setInitializing(false);
    };
    useEffect(() => {
        // Apps will take care of initializing their data
        if (!location.pathname.startsWith(APPS_ROUTE)) {
            initializeApp();
        } else setInitializing(false);
    }, []);

    return initializing ? (
        <AppLoadingPage loadingText="Loading App..." />
    ) : dGroupAttr.subscription && dGroupAttr.subscription?.status !== "live" ? (
        <SubscriptionIssuePage /> /*: (dGroupAttr as TFullDealerGroupAttr).initialSetupStatus !== "done" &&
      (dGroupAttr as TFullDealerGroupAttr).initialSetupStatus !== "canceled" ? (
        <WelcomePage />
    )*/
    ) : (
        <Switch>
            <Route path={CONTACTS_ROUTE} component={() => <ContactsPage />} />
            <Route path={DOCUMENTS_ROUTE} component={() => <DocumentsPage />} />
            <Route path={DASHBOARD_ROUTE} component={() => <DashboardPage />} />
            {/* <Route path={DIARY_ROUTE} component={() => <DiaryPage />} /> */}
            <Route path={SERVICES_FEES_ROUTE} component={() => <ServicesFeesPage />} />
            <Route path={TASKS_ROUTE} component={() => <TasksPage />} />
            <Route path={PROJECTS_ROUTE} component={() => <TasksPage />} />
            <Route path={SETTINGS_ROUTE} component={() => <SettingsPage />} />
            <Route path={APPS_ROUTE} component={() => <AusplanAppsPage />} />
            <Route path={ORGANIZATIONS_ROUTE} component={() => <AuthorizedOrganizationsPage />} />
            <Redirect to={DASHBOARD_ROUTE} push />
        </Switch>
    );
};

const ErrorDisplay: React.FC<{ text: string }> = ({ text }) => {
    const handleReloadPage = () => {
        window.location.reload();
    };
    return (
        <div className={Styles.ErrorContainer}>
            <div className={Styles.ErrorTitle}>Error!</div>
            <div className={Styles.ErrorText}>{text}</div>
            <div>
                Try{" "}
                <Button size="small" onClick={handleReloadPage}>
                    Reloading
                </Button>{" "}
                the page. If that does not help, please contact technical support with a screenshot of this error
            </div>
        </div>
    );
};

const NoOrgDisplay = () => {
    return (
        <div className={Styles.ErrorContainer}>
            <div className={Styles.ErrorTitle}>Sorry!</div>
            <div className={Styles.ErrorText}>You currently do not belong to any organization and hence cannot use Ausplan.</div>
            <div>If you think there is something wrong, please contact technical support</div>
        </div>
    );
};

const SelectDealerGroupView: React.FC<{ onSelectOrg: (org: DealerGroup) => void }> = ({ onSelectOrg }) => {
    const userDealerGroups = useSelector<RootState, DealerGroup[]>((state) => state.dealerGroup.dealerGroups);
    return (
        <div className={Styles.OrgsViewContainer}>
            <div className={Styles.SelectOrgTitle}>Select Dealer Group</div>
            <div className={Styles.SelectOrgDesc}>You currently belong to these Dealer Groups. Please select one to proceed.</div>
            <div className={Styles.OrgListContainer}>
                {userDealerGroups.map((t) => (
                    <div key={t.id} className={Styles.OrgItemContainer} onClick={() => onSelectOrg(t)}>
                        <Avatar shape="square">{t.data.attrs.name[0]}</Avatar>
                        <div className={Styles.Text}>{t.data.attrs.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default () => {
    const userDataLoaded = useSelector<RootState, boolean>((state) => state.user.userLoaded);
    const activeDGroup = useSelector<RootState, DealerGroup | undefined>((state) => state.dealerGroup.activeDealerGroup);
    const activeOrg = useSelector<RootState, Organization | undefined>((state) => state.orgs.activeOrg);
    const userDgroups = useSelector<RootState, DealerGroup[]>((state) => state.dealerGroup.dealerGroups);
    const [loading, setLoading] = useState(!userDataLoaded);
    const location = useLocation();
    const [loadError, setLoadError] = useState<string>();
    const handleSetActiveDGroup = async (org: DealerGroup) => {
        setLoading(true);
        await AppManager.dealerGroup.activateDealerGroup(org, (err) => {
            setLoading(false);
            if (err) setLoadError(err);
        });
    };
    const handleSetActiveOrganization = async (org: Organization) => {
        setLoading(true);
        await AppManager.org.activateOrganization(org, (err) => {
            setLoading(false);
            if (err) setLoadError(err);
        });
    };
    useEffect(() => {
        if (!userDataLoaded) {
            AppManager.user.apiFetchUserData(async (err) => {
                setLoading(false);
                if (err) setLoadError(err);
                else {
                    const persistedDgroup = AppManager.dealerGroup.getPersistedActiveDGroup();
                    if (persistedDgroup) await handleSetActiveDGroup(persistedDgroup);
                    else if (AppManager.dealerGroup.getUserDGroups().length == 1) {
                        await handleSetActiveDGroup(AppManager.dealerGroup.getUserDGroups()[0]);
                    }

                    const persistedOrg = AppManager.org.getPersistedActiveOrg();
                    if (persistedOrg) await handleSetActiveOrganization(persistedOrg);
                }
            });
        }
    }, [userDataLoaded]);

    useEffect(() => {
        if (activeDGroup) AppManager.notifications.apiFetchUserNotifications(() => {});
    }, [activeDGroup]);

    return loading ? (
        <AppLoadingPage simple={location.pathname.startsWith(APPS_ROUTE)} />
    ) : loadError ? (
        <AppSimplePage content={<ErrorDisplay text={loadError} />} />
    ) : !userDgroups.length ? (
        <AppSimplePage content={<NoOrgDisplay />} />
    ) : !activeDGroup ? (
        <AppSimplePage content={<SelectDealerGroupView onSelectOrg={handleSetActiveDGroup} />} />
    ) : !activeOrg ? (
        <AppSimplePage content={<SelectOrganizationView onSelectOrg={handleSetActiveOrganization} />} />
    ) : (
        <ProtectedRoutes />
    );
};
